import { IssueCaseDoc, OrderDoc } from '@gooduncles/gu-app-schema';
import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export interface SelectedStore {
  id: string;
  name: string;
}

interface CrmBoardContextValue {
  // 매장 선택 (전체 연동)
  selectedStore: SelectedStore | null;
  setSelectedStore: (store: SelectedStore | null) => void;

  // 연결된 주문 (생성 폼용)
  linkedOrder: OrderDoc | null;
  setLinkedOrder: (order: OrderDoc | null) => void;

  // 연결된 이슈 케이스 (생성 폼용)
  linkedIssueCase: IssueCaseDoc | null;
  setLinkedIssueCase: (issueCase: IssueCaseDoc | null) => void;
}

const CrmBoardContext = createContext<CrmBoardContextValue | null>(null);

export const useCrmBoardContext = () => {
  const context = useContext(CrmBoardContext);
  if (!context) {
    throw new Error('useCrmBoardContext must be used within CrmBoardProvider');
  }
  return context;
};

interface CrmBoardProviderProps {
  children: ReactNode;
}

export const CrmBoardProvider: FC<CrmBoardProviderProps> = ({ children }) => {
  const [selectedStore, setSelectedStore] = useState<SelectedStore | null>(null);
  const [linkedOrder, setLinkedOrder] = useState<OrderDoc | null>(null);
  const [linkedIssueCase, setLinkedIssueCase] = useState<IssueCaseDoc | null>(null);

  // 매장 변경 시 연결된 주문/이슈 초기화
  const handleSetSelectedStore = useCallback((store: SelectedStore | null) => {
    setSelectedStore(store);
    setLinkedOrder(null);
    setLinkedIssueCase(null);
  }, []);

  const value = useMemo(
    () => ({
      selectedStore,
      setSelectedStore: handleSetSelectedStore,
      linkedOrder,
      setLinkedOrder,
      linkedIssueCase,
      setLinkedIssueCase,
    }),
    [selectedStore, handleSetSelectedStore, linkedOrder, linkedIssueCase],
  );

  return <CrmBoardContext.Provider value={value}>{children}</CrmBoardContext.Provider>;
};

export default CrmBoardContext;
